import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Badge } from '../ForBrands/ui/BrandUI';
import { coverageKind, coverageSentence, possessive } from '../ForAthletes/teamCoverage';

// The plan an account is on, as a badge (Kobi, 2026-10-07: an athlete who
// joins a team whose coach is on a paid plan "has premium status on his/her
// profile showing paid").
//
// `plan` is either the Redux entitlement slice (GET /entitlement/:
// entitlement, entitlementSource, coverage {coachName, teamName, kind,
// sponsorName, ...}) or a row of the admin plans endpoint (POST
// admin/users/plans/: entitlement, source, label, coverage {coachName,
// coachEmail, teamName, kind, sponsorName, ...}, endsAt). Either way
// `entitlement` is the effective one: an athlete covered by their team is
// athlete_premium with source 'coach' (the coach's plan), 'team_plan' (a
// team plan CACHE set up) or 'team_code' (a team code that waives the fee).

export const ATHLETE_PREMIUM = 'athlete_premium';
const COACH_PLANS = { coach_core: 'Core', coach_pro: 'Pro', coach_premium: 'Premium Team' };
const COACH_DISPLAY = { coach_core: 'Coach Core', coach_pro: 'Coach Pro', coach_premium: 'Coach Premium Team' };
const TEAM_SOURCES = new Set(['coach', 'team_plan', 'team_code']);

const sourceOf = (plan) => plan?.entitlementSource ?? plan?.source ?? null;

// The signed-in account's plan from the Redux entitlement slice. A store
// with no slice (or before the first answer) gives no entitlement.
export const selectPlan = (state) => ({
  entitlement: state?.entitlement?.entitlement || null,
  entitlementSource: state?.entitlement?.entitlementSource || null,
  coverage: state?.entitlement?.coverage || null,
  displayName: state?.entitlement?.displayName || null,
});
export const usePlan = () => useSelector(selectPlan, shallowEqual);

export const isPremium = (plan) => plan?.entitlement === ATHLETE_PREMIUM;

// Premium included with the team: a coach on Core, Pro or Premium, a team
// plan, or a team code that waives the fee.
export const isTeamPlan = (plan) => isPremium(plan) && TEAM_SOURCES.has(sourceOf(plan));

// What includes a covered athlete's Premium: the coverage's kind, else the
// source ('coach' for payloads from before team plans).
export const includedKind = (plan) => {
  const source = sourceOf(plan);
  if (source === 'team_plan' || source === 'team_code') return source;
  return coverageKind(plan?.coverage);
};

// The badge and menu word for it: "Team code" for a code, else "Team plan".
export const includedLabel = (plan) => (includedKind(plan) === 'team_code' ? 'Team code' : 'Team plan');

const formatDay = (iso) => {
  const when = iso ? new Date(iso) : null;
  if (!when || Number.isNaN(when.getTime())) return '';
  return when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

// The admin title of a covered athlete: "Included with Vic Coach's Longhorns
// plan", "Included with Ridgeview Elite's team plan" or "Included with the
// Hillcrest Academy team code". A missing coach, sponsor or team name still
// reads as a sentence.
export const includedTitle = (coverage) => {
  const kind = coverageKind(coverage);
  const team = String(coverage?.teamName || '').trim();
  if (kind === 'team_plan') {
    const sponsor = String(coverage?.sponsorName || team).trim();
    return sponsor ? `Included with ${possessive(sponsor)} team plan` : 'Included with a team plan';
  }
  if (kind === 'team_code') {
    const named = String(coverage?.sponsorName || team).trim();
    return named ? `Included with the ${named} team code` : 'Included with a team code';
  }
  const coach = String(coverage?.coachName || coverage?.coachEmail || '').trim();
  return `Included with ${coach ? `${coach}'s` : "the coach's"} ${team || 'team'} plan`;
};

// The admin title of a plan the account holds itself: where it comes from
// and, for a plan CACHE set up, until when.
const ownTitle = (plan, name) => {
  const until = formatDay(plan?.endsAt);
  const source = sourceOf(plan);
  if (source === 'stripe') return `${name}, paid through Stripe`;
  if (source === 'promo') return `${name}, promo code${until ? ` until ${until}` : ''}`;
  return `${name}, set up by CACHE${until ? ` until ${until}` : ''}`;
};

// The avatar menu line: "Athlete Premium · Team plan" (or "· Team code"),
// "Athlete Premium" or "Free plan"; '' until the entitlement has loaded.
export const planLine = (plan) => {
  if (!plan?.entitlement) return '';
  if (isTeamPlan(plan)) return `Athlete Premium · ${includedLabel(plan)}`;
  if (isPremium(plan)) return 'Athlete Premium';
  return 'Free plan';
};

// What the "open as user" banner adds after the account: " · Premium (team
// plan)", " · Premium (team code)", " · Premium" or " · Free plan" for an
// athlete, the plan's name for a coach on one; '' until the entitlement has
// loaded, and for staff.
export const bannerPlan = (plan) => {
  const entitlement = plan?.entitlement;
  if (!entitlement || entitlement === 'admin' || entitlement === 'public') return '';
  if (isTeamPlan(plan)) return ` · Premium (${includedLabel(plan).toLowerCase()})`;
  if (isPremium(plan)) return ' · Premium';
  if (COACH_DISPLAY[entitlement]) return ` · ${COACH_DISPLAY[entitlement]}`;
  return ' · Free plan';
};

// What the badge shows: { badges: [{ label, tone }], title }, or null for no
// badge (still loading, a free plan, staff).
//   - Premium: an orange "Premium", plus "Team plan" when the team's coach
//     or a team plan pays for it ("Team code" when a team code waives the
//     fee); `compact` (tables) folds both into one "Premium · Team plan"
//     badge. The title says what includes it.
//   - `admin`: a coach's own paid plan by its server label (Core, Pro,
//     Premium Team), and titles that name the source and the end date.
export const planBadge = (plan, { admin = false, compact = false } = {}) => {
  const entitlement = plan?.entitlement;
  if (!entitlement) return null;
  if (isPremium(plan)) {
    const team = isTeamPlan(plan);
    let title;
    if (team) title = admin ? includedTitle(plan.coverage) : coverageSentence(plan.coverage);
    else title = admin ? ownTitle(plan, 'Athlete Premium') : 'Athlete Premium';
    let badges = [{ label: 'Premium', tone: 'brand' }];
    if (team) {
      const label = includedLabel(plan);
      badges = compact ? [{ label: `Premium · ${label}`, tone: 'brand' }] : [...badges, { label, tone: 'outline' }];
    }
    return { badges, title };
  }
  const coachPlan = COACH_PLANS[entitlement];
  if (admin && coachPlan) {
    const label = plan.label && plan.label !== 'Free' ? plan.label : coachPlan;
    return { badges: [{ label, tone: 'soft' }], title: ownTitle(plan, `Coach ${label}`) };
  }
  return null;
};

const PlanBadge = ({ plan, admin = false, compact = false, className = '' }) => {
  const shown = planBadge(plan, { admin, compact });
  if (!shown) return null;
  // Badge takes no title, so the wrapper carries it (as CohortBadge does).
  return (
    <span className={`inline-flex items-center gap-1 ${className}`.trim()} title={shown.title} data-testid="plan-badge">
      {shown.badges.map((b) => (
        <Badge key={b.label} tone={b.tone}>{b.label}</Badge>
      ))}
    </span>
  );
};

export default PlanBadge;
